import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Youtube, Play } from "lucide-react";
import { Button } from "./ui/button";
import { useVideoFeed } from './useVideoFeed';
import { SOCIAL_URLS } from '../content/socials';

const relativeTime = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (isNaN(then)) return "";
  const diff = Math.max(0, Date.now() - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return mins <= 1 ? "just now" : `${mins} minutes ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return hrs === 1 ? "1 hour ago" : `${hrs} hours ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return days === 1 ? "1 day ago" : `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return months === 1 ? "1 month ago" : `${months} months ago`;
  const years = Math.floor(months / 12);
  return years === 1 ? "1 year ago" : `${years} years ago`;
};

const LatestVideos = () => {
  const scroller = useRef(null);
  const { feed, error } = useVideoFeed();
  const channelUrl = feed?.channel_url || SOCIAL_URLS.youtube;
  const channelName = feed?.channel_names?.join(' & ') || 'our channels';
  const [capacity, setCapacity] = useState(1), [start, setStart] = useState(0);
  useEffect(() => {
    const element = scroller.current;
    if (!element) return;
    const measure = () => setCapacity(Math.max(1, Math.floor((element.clientWidth + 16) / 256)));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const list = feed?.videos || [];

  const first = Math.min(start, Math.max(0, list.length - capacity));
  const scrollBy = (dir) => setStart(Math.max(0, Math.min(list.length - capacity, first + dir * capacity)));

  return (
    <section id="videos" className="max-w-6xl mx-auto w-full px-4 sm:px-6 py-14">
      <div className="rounded-2xl glass p-5 sm:p-7" data-testid="latest-videos">
        {/* header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="min-w-0">
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-white" data-testid="latest-videos-title">
              Latest Videos
            </h2>
            <p className="text-sm text-slate-400 mt-1" data-testid="latest-videos-channel">
              New uploads from{" "}
              <span className="text-emerald-300 font-semibold">{channelName}</span>{" "}
              on YouTube
            </p>
            {feed?.updated_at && <p className="text-xs text-slate-500 mt-2" data-testid="videos-last-updated">Updated {relativeTime(feed.updated_at)}</p>}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => scrollBy(-1)}
              disabled={first === 0}
              aria-label="Previous videos"
              data-testid="videos-prev"
              className="h-10 w-10 flex items-center justify-center rounded-lg bg-white/5 ring-1 ring-white/10 text-slate-300 hover:bg-white/10 hover:text-white transition-colors disabled:opacity-35 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={() => scrollBy(1)}
              disabled={first + capacity >= list.length}
              aria-label="Next videos"
              data-testid="videos-next"
              className="h-10 w-10 flex items-center justify-center rounded-lg bg-white/5 ring-1 ring-white/10 text-slate-300 hover:bg-white/10 hover:text-white transition-colors disabled:opacity-35 disabled:cursor-not-allowed"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
            <Button
              asChild
              data-testid="videos-subscribe"
              className="ml-1 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
            >
              <a href={channelUrl} target="_blank" rel="noopener noreferrer">
                <Youtube className="mr-2 h-4 w-4" />
                Subscribe
              </a>
            </Button>
          </div>
        </div>

        {(error || feed?.stale) && <p role="status" className="text-xs text-amber-300 mb-4" data-testid="videos-refresh-status">{error || (list.length ? 'Showing saved uploads while a channel reconnects.' : 'Fetching channel uploads. Automatic retries are enabled.')}</p>}
        {!list.length && <p className="text-sm text-slate-400 mb-4" data-testid="videos-empty-state">{!feed ? (error ? 'Videos are temporarily unavailable.' : 'Loading latest uploads…') : feed.configured ? 'No videos available yet.' : 'No channels configured yet.'}</p>}
        {/* cards */}
        <div
          ref={scroller}
          className="grid gap-4 pb-1"
          style={{ gridTemplateColumns: `repeat(${capacity}, minmax(0, 1fr))` }}
          data-testid="latest-videos-grid"
        >
          {list.slice(first, first + capacity).map((v) => (
            <a
              key={v.id}
              href={v.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`video-card-${v.id}`}
              className="group min-w-0"
            >
              <div className="relative rounded-xl overflow-hidden ring-1 ring-white/10 aspect-video bg-black/40">
                <img
                  src={v.thumb}
                  alt={v.title}
                  loading="lazy"
                  data-testid={`video-thumbnail-${v.id}`}
                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="pointer-events-none absolute inset-0 bg-[#0b1120]/55 transition-opacity duration-300 group-hover:opacity-0" />
                <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500 text-emerald-950 shadow-lg">
                    <Play className="h-5 w-5 fill-current ml-0.5" />
                  </span>
                </div>
              </div>
              <h3 data-testid={`video-title-${v.id}`} className="mt-3 text-sm font-bold text-white leading-snug line-clamp-2 group-hover:text-emerald-300 transition-colors">
                {v.title}
              </h3>
              <p data-testid={`video-date-${v.id}`} className="mt-1 text-xs text-slate-500">
                {v.channel}{(v.ageText || v.published) ? ` · ${v.ageText || relativeTime(v.published)}` : ''}
              </p>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default LatestVideos;
